import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { IP } from "../configuracion.js";
import "../styles/ReimprimirTicket.css";

const ReimprimirTicket = ({ 
  onClose,
  currentUser,
  property,
  deviceConfig,
  devices,
  printTicket,
  mostrarMensaje
}) => {
  const [chequeNumber, setChequeNumber] = useState("");
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [error, setError] = useState(null);
  const [chequeInfo, setChequeInfo] = useState(null);

  // Cargar impresoras al montar el componente
  useEffect(() => {
    if (property) {
      fetchPrinters();
    }
  }, [property]);

  const fetchPrinters = async () => {
    if (!property) return;

    setLoadingPrinters(true);
    try {
      // ✅ CORREGIDO: Usar property directamente SIN encodeURIComponent
      const printersUrl = `${IP}/tavpos/backend/data/${property}/printers.json`;
      
      console.log("Solicitando impresoras desde:", printersUrl);

      const response = await fetch(printersUrl);

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const data = await response.json();
      
      let printersList = [];
      
      if (Array.isArray(data)) {
        printersList = data;
      } else if (data.printers && Array.isArray(data.printers)) {
        printersList = data.printers;
      } else if (typeof data === 'object') {
        printersList = Object.values(data);
      }
      
      const cleanPrinters = printersList
        .filter(printer => printer && printer.name)
        .map(printer => ({
          id: printer.id || printer.name,
          name: printer.name
        }));

      setPrinters(cleanPrinters);

      if (cleanPrinters.length > 0) {
        setSelectedPrinter(cleanPrinters[0].name);
      }

    } catch (err) {
      console.error("Error cargando impresoras:", err);
      // Usar impresoras por defecto
      const defaultPrinters = [
        { id: 1, name: "PNTSterling" },
        { id: 11, name: "POS-58" },
        { id: 12, name: "tavpos1" }
      ];
      setPrinters(defaultPrinters);
      setSelectedPrinter("POS-58");
    } finally {
      setLoadingPrinters(false);
    }
  };

  const handleNumberClick = (number) => {
    if (chequeNumber.length < 10) {
      setChequeNumber(prev => prev + number);
      setError(null);
    }
  };

  const handleClear = () => {
    setChequeNumber("");
    setChequeInfo(null);
    setError(null);
  };

  const handleBackspace = () => {
    setChequeNumber(prev => prev.slice(0, -1));
    setError(null);
  };

  const handleReimprimir = async () => {
    if (!chequeNumber.trim()) {
      setError("Ingresa un número de cheque");
      return;
    }

    if (!selectedPrinter) {
      setError("Selecciona una impresora");
      return;
    }

    if (!property) {
      setError("Error: No se recibió la propiedad");
      return;
    }

    if (!currentUser || !currentUser.nombre) {
      setError("Error: No se identificó al usuario");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new URLSearchParams();
      formData.append("property", property);
      formData.append("cheque", chequeNumber);
      formData.append("printer", selectedPrinter);
      formData.append("usuario", currentUser.nombre);

      console.log("Enviando datos:", {
        property,
        cheque: chequeNumber,
        printer: selectedPrinter,
        usuario: currentUser.nombre
      });

      // ✅ CORREGIDO: Usar property DIRECTAMENTE, SIN encodeURIComponent
      const url = `https://www.bts.ei-solutions.co/tavpos/backend/data/${property}/ticketsr.php`;
      
      console.log("URL de destino:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString()
      });

      console.log("Estado de respuesta:", response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}: ${response.statusText}`);
      }

      const responseText = await response.text();
      console.log("Respuesta del servidor:", responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parseando JSON:", parseError);
        throw new Error("El servidor respondió con un formato no válido");
      }

      console.log("Datos parseados:", data);

      if (!data.success) {
        throw new Error(data.message || "Error en el servidor");
      }

      const chequeData = data.data;
      setChequeInfo(chequeData);

      mostrarMensaje(`✅ Ticket reimpreso - Cheque #${chequeData.cheque || chequeNumber} en ${selectedPrinter} por ${currentUser.nombre}`);
      
      handleClear();

    } catch (err) {
      console.error("Error completo:", err);
      let errorMessage = err.message;
      
      if (err.message.includes("Failed to fetch")) {
        errorMessage = "Error de conexión. Verifica tu conexión a internet.";
      }
      
      setError(errorMessage);
      mostrarMensaje(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fechaString) => {
    if (!fechaString) return "No disponible";
    try {
      const fecha = new Date(fechaString);
      return fecha.toLocaleString('es-MX');
    } catch {
      return fechaString;
    }
  };

  const calcularTotal = (cheque) => {
    if (cheque && cheque.total) return parseFloat(cheque.total).toFixed(2);
    return "0.00";
  };

  return (
    <div className="reimprimir-ticket-modal">
      <div className="reimprimir-ticket-content">
        <div className="reimprimir-header">
          <h2>Reimprimir Ticket</h2>
          <button className="close-button" onClick={onClose} disabled={loading}>
            ×
          </button>
        </div>

        <div className="reimprimir-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {loading && (
            <div className="loading-overlay">
              Reimprimiendo ticket en {selectedPrinter}...
            </div>
          )}

          {/* Información del usuario */}
          <div className="user-info-section">
            <p><strong>Usuario:</strong> {currentUser?.nombre || 'No identificado'}</p>
          </div>

          {/* Selector de Impresora */}
          <div className="printer-section">
            <label>Seleccionar Impresora:</label>
            {loadingPrinters ? (
              <div className="loading-printers">Cargando impresoras...</div>
            ) : (
              <select
                value={selectedPrinter}
                onChange={(e) => setSelectedPrinter(e.target.value)}
                className="printer-select"
                disabled={loading || printers.length === 0}
              >
                {printers.length === 0 ? (
                  <option value="">No hay impresoras disponibles</option>
                ) : (
                  printers.map(printer => (
                    <option key={printer.id} value={printer.name}>
                      {printer.name}
                    </option>
                  ))
                )}
              </select>
            )}
          </div>

          {/* Input de Número de Cheque */}
          <div className="input-section">
            <label>Número de Cheque:</label>
            <div className="cheque-input-container">
              <input
                type="text"
                value={chequeNumber}
                readOnly
                placeholder="Ingresa el número de cheque"
                className="cheque-input"
              />
              {chequeNumber && (
                <button 
                  className="clear-input" 
                  onClick={handleClear}
                  type="button"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Información del Cheque Encontrado */}
          {chequeInfo && (
            <div className="cheque-preview">
              <h3>Cheque Procesado:</h3>
              <div className="cheque-details">
                <div className="detail-row">
                  <span>Cheque #:</span>
                  <strong>{chequeInfo.cheque || chequeNumber}</strong>
                </div>
                <div className="detail-row">
                  <span>Impresora:</span>
                  <span>{selectedPrinter}</span>
                </div>
                <div className="detail-row">
                  <span>Usuario:</span>
                  <span>{currentUser.nombre}</span>
                </div>
                <div className="detail-row">
                  <span>Total:</span>
                  <strong>${calcularTotal(chequeInfo)}</strong>
                </div>
                <div className="detail-row">
                  <span>Artículos:</span>
                  <span>{chequeInfo.items_count || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Teclado Numérico */}
          <div className="numeric-keypad">
            <div className="keypad-row">
              <button className="keypad-button" onClick={() => handleNumberClick("1")} disabled={loading}>1</button>
              <button className="keypad-button" onClick={() => handleNumberClick("2")} disabled={loading}>2</button>
              <button className="keypad-button" onClick={() => handleNumberClick("3")} disabled={loading}>3</button>
              <button className="keypad-button" onClick={() => handleNumberClick("4")} disabled={loading}>4</button>
              <button className="keypad-button" onClick={() => handleNumberClick("5")} disabled={loading}>5</button>
              <button className="keypad-button" onClick={() => handleNumberClick("6")} disabled={loading}>6</button>
              <button className="keypad-button" onClick={() => handleNumberClick("7")} disabled={loading}>7</button>
              <button className="keypad-button" onClick={() => handleNumberClick("8")} disabled={loading}>8</button>
              <button className="keypad-button" onClick={() => handleNumberClick("9")} disabled={loading}>9</button>
              <button className="keypad-button clear-button" onClick={handleClear} disabled={loading}>C</button>
              <button className="keypad-button" onClick={() => handleNumberClick("0")} disabled={loading}>0</button>
              <button className="keypad-button backspace-button" onClick={handleBackspace} disabled={loading}>⌫</button>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="action-buttons">
            <button
              className="reimprimir-button"
              onClick={handleReimprimir}
              disabled={!chequeNumber || !selectedPrinter || loading || !currentUser?.nombre}
            >
              {loading ? "Imprimiendo..." : "Reimprimir Ticket"}
            </button>
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ReimprimirTicket.propTypes = {
  onClose: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  property: PropTypes.string.isRequired,
  deviceConfig: PropTypes.object.isRequired,
  devices: PropTypes.array.isRequired,
  printTicket: PropTypes.func.isRequired,
  mostrarMensaje: PropTypes.func.isRequired,
};

export default ReimprimirTicket;